import type { ReactNode } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { cn } from '../../../lib/utils';

export interface ThreadBubbleProps {
  side: 'ours' | 'theirs';
  /** The message's status, as a MessageTag. */
  tag: ReactNode;
  /** "You" for ours; the creator's name for theirs. */
  author: string;
  /** Figma's Address slot: "via <mailbox address>" for ours, the sender's address for theirs. */
  address?: string;
  /** Pills that read with the sender (a reply's intent), placed right after the Address. */
  extra?: ReactNode;
  timestamp: string;
  /** Hover text on the time (the email's own Date, when it differs from the arrival shown). */
  timestampTitle?: string;
  avatarUrl?: string;
  /** The name the avatar's initials (and alt text) come from. */
  avatarLabel: string;
  children: ReactNode;
}

/** Figma's message time: "29 Aug, 10:02", in the viewer's zone. */
export function fmtThreadTime(iso?: string): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

function initials(label: string): string {
  return label
    .split(' ')
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// Figma 170:4090 — Direction=Sent: brand stroke, brand-50 head over a stone-200 rule; Received: neutral stroke, #fafafa head over neutral-100.
// The `!` beats globals.css's unlayered `* { border-color }`.
const SIDE = {
  ours: { card: 'border-(--brand-500)!', head: 'bg-(--brand-50) border-b-(--stone-200)!' },
  theirs: { card: 'border-border', head: 'bg-(--background-primary-light) border-b-neutral-100!' },
} as const;

/** One thread message, laid out as Figma's "Thread / Message — Email": avatar outside, then a card with a head strip (Tag · Author · Address … Time) over the body. */
export function ThreadBubble({ side, tag, author, address, extra, timestamp, timestampTitle, avatarUrl, avatarLabel, children }: ThreadBubbleProps) {
  const style = SIDE[side];
  return (
    <div data-bubble={side} className="flex w-full items-start gap-3">
      <Avatar size="sm" aria-hidden className="mt-0.5 shrink-0">
        {avatarUrl && <AvatarImage src={avatarUrl} alt="" />}
        <AvatarFallback>{initials(avatarLabel)}</AvatarFallback>
      </Avatar>
      <div data-part="card" className={cn('min-w-0 flex-1 overflow-hidden rounded-xl border bg-card', style.card)}>
        <div data-part="head" className={cn('flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2', style.head)}>
          <div className="flex min-w-0 items-center gap-2">
            {tag}
            <span className="shrink-0 text-sm/5 font-medium text-foreground">{author}</span>
            {address && (
              <span data-part="address" title={address} className="truncate text-[12px] text-muted-foreground">
                {address}
              </span>
            )}
            {extra}
          </div>
          <span title={timestampTitle} className="shrink-0 text-[12px] text-muted-foreground">{timestamp}</span>
        </div>
        <div data-part="body" className="p-4">
          {children}
        </div>
      </div>
    </div>
  );
}
